import { useState, useRef, Fragment } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../lib/api";
import { confBadge } from "../lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { CloudArrowUp, CircleNotch, Eye, ListMagnifyingGlass, Sparkle } from "@phosphor-icons/react";
import { DocPreviewDialog } from "../components/DocPreview";
import { ValidationDesk } from "../components/ValidationDesk";

export function UploadZone({ engagements, onUploaded }) {
  const [engId, setEngId] = useState("");
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  const upload = useMutation({
    mutationFn: (file) => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("engagement_id", engId);
      return api.post("/documents/upload", fd);
    },
    onSuccess: () => { toast.success("Uploaded — AI classification in progress"); onUploaded?.(); },
    onError: (e) => toast.error(errText(e)),
  });

  const handleFiles = (files) => {
    if (!engId) { toast.error("Select an engagement first"); return; }
    [...files].forEach((f) => upload.mutate(f));
  };

  return (
    <div className="space-y-3">
      <Select value={engId} onValueChange={setEngId}>
        <SelectTrigger data-testid="upload-engagement-select" className="bg-white"><SelectValue placeholder="Select engagement for upload" /></SelectTrigger>
        <SelectContent>
          {(engagements || []).filter((e) => e.stage !== "archived").map((e) => (
            <SelectItem key={e.id} value={e.id}>{e.client_name} — {e.service_type} {e.tax_year}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div data-testid="upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`rounded-lg border-2 border-dashed ${dragging ? "border-zinc-500 bg-zinc-100" : "border-zinc-300 bg-white"} p-8 text-center cursor-pointer transition-colors duration-150`}>
        {upload.isPending ? <CircleNotch size={24} className="animate-spin mx-auto text-zinc-400" /> : <CloudArrowUp size={24} className="mx-auto text-zinc-400" />}
        <p className="text-sm text-zinc-600 mt-2">Drop tax documents here or click to browse</p>
        <p className="text-xs text-zinc-400 mt-1">W-2, 1099s, K-1, brokerage statements, QuickBooks reports… auto-classified by AI</p>
        <input ref={inputRef} type="file" multiple className="hidden" data-testid="upload-file-input" onChange={(e) => handleFiles(e.target.files)} />
      </div>
    </div>
  );
}

export default function DocumentsPage() {
  const qc = useQueryClient();
  const [previewDoc, setPreviewDoc] = useState(null);
  const [validateDoc, setValidateDoc] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const { data: engagements } = useQuery({ queryKey: ["engagements"], queryFn: () => api.get("/engagements").then((r) => r.data) });
  const { data: docs } = useQuery({
    queryKey: ["documents"],
    queryFn: () => api.get("/documents").then((r) => r.data),
    refetchInterval: (q) => (q.state.data || []).some((d) => d.status === "processing") ? 3000 : false,
  });

  return (
    <div className="p-8 space-y-6 fade-up" data-testid="documents-page">
      <div>
        <h1 className="font-heading text-2xl font-medium tracking-tight text-zinc-900">Document Intake</h1>
        <p className="text-sm text-zinc-500 mt-1">AI-powered OCR classification · SSN/EIN redacted in previews · version controlled</p>
      </div>

      <UploadZone engagements={engagements} onUploaded={() => qc.invalidateQueries({ queryKey: ["documents"] })} />

      <div className="rounded-lg border border-zinc-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50">
              <TableHead className="text-xs">File</TableHead>
              <TableHead className="text-xs">Classification</TableHead>
              <TableHead className="text-xs">Confidence</TableHead>
              <TableHead className="text-xs">Extracted</TableHead>
              <TableHead className="text-xs">Ver</TableHead>
              <TableHead className="text-xs">Uploaded by</TableHead>
              <TableHead className="text-xs w-10"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(docs || []).map((d) => (
              <Fragment key={d.id}>
              <TableRow data-testid={`doc-row-${d.id}`} className="hover:bg-zinc-50 transition-colors duration-150">
                <TableCell className="font-mono text-xs text-zinc-900 max-w-[200px] truncate">{d.filename}</TableCell>
                <TableCell>
                  {d.status === "processing"
                    ? <span className="text-xs text-zinc-400 flex items-center gap-1"><CircleNotch size={12} className="animate-spin" /> classifying…</span>
                    : <span className={`text-[11px] px-1.5 py-0.5 rounded-sm ${confBadge(d.confidence)}`}>{d.doc_type || "Failed"}</span>}
                </TableCell>
                <TableCell className="font-mono text-xs">{d.confidence != null ? `${Math.round(d.confidence * 100)}%` : "—"}</TableCell>
                <TableCell className="text-xs text-zinc-500 max-w-[280px] truncate">{d.summary || Object.entries(d.extracted_fields || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}</TableCell>
                <TableCell className="font-mono text-xs">v{d.version}</TableCell>
                <TableCell className="text-xs text-zinc-500">{d.uploaded_by}</TableCell>
                <TableCell>
                  {(d.ai_summary_bullets || []).length > 0 && (
                    <button data-testid={`doc-summary-btn-${d.id}`} title="AI submission summary" onClick={() => setExpanded(expanded === d.id ? null : d.id)}
                      className={`${expanded === d.id ? "text-indigo-600" : "text-zinc-400"} hover:text-indigo-600 transition-colors duration-150`}><Sparkle size={15} /></button>
                  )}
                  <button data-testid={`doc-validate-btn-${d.id}`} title="Validation desk — audit AI extraction side-by-side" onClick={() => setValidateDoc(d.id)} className="text-zinc-400 hover:text-zinc-900 transition-colors duration-150"><ListMagnifyingGlass size={15} /></button>
                  <button data-testid={`doc-preview-btn-${d.id}`} title="Preview / download" onClick={() => setPreviewDoc(d)} className="text-zinc-400 hover:text-zinc-900 transition-colors duration-150"><Eye size={15} /></button>
                </TableCell>
              </TableRow>
              {expanded === d.id && (
                <TableRow className="bg-indigo-50/40 hover:bg-indigo-50/40">
                  <TableCell colSpan={7}>
                    <div data-testid={`doc-summary-panel-${d.id}`} className="py-1">
                      <p className="text-[10px] uppercase tracking-wide text-indigo-500 mb-1.5 flex items-center gap-1"><Sparkle size={11} /> AI submission summary</p>
                      <ul className="space-y-1">
                        {(d.ai_summary_bullets || []).map((b, i) => <li key={`${d.id}-b-${i}-${b.slice(0, 20)}`} className="text-xs text-zinc-700 flex gap-1.5"><span className="text-indigo-400">•</span>{b}</li>)}
                      </ul>
                    </div>
                  </TableCell>
                </TableRow>
              )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
        {docs?.length === 0 && <p className="text-xs text-zinc-400 italic p-4">No documents uploaded yet.</p>}
      </div>
      <DocPreviewDialog doc={previewDoc} open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)} />
      <ValidationDesk docId={validateDoc} open={!!validateDoc} onOpenChange={(o) => !o && setValidateDoc(null)} />
    </div>
  );
}
